/* eslint-disable max-len, newline-per-chained-call, id-length */
import Papa from 'papaparse';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const SUPPORTED_DATE_FORMATS = ['DD-MMM-YYYY', 'D-MMM-YYYY'];

export const FILE_UPLOAD_TYPES = {
  REDMINE: 'redmine',
  LEAVE: 'leave',
  LEAVE_TEST: 'leave-test',
  HOLIDAY: 'holiday',
  SALARY: 'salary',
  PROJECT_EXPENSE: 'project-expense',
  JIRA: 'jira',
};

const trimString = (value) => String(value ?? '').trim();
const normalizeHeader = (value) => trimString(value).toLowerCase();

const getMissingHeaders = (headers, expectedHeaders) => {
  const headerSet = new Set((headers || []).map(normalizeHeader));
  return expectedHeaders.filter((header) => !headerSet.has(normalizeHeader(header)));
};

const buildHeaderError = (headers, expectedHeaders) => {
  const missingHeaders = getMissingHeaders(headers, expectedHeaders);
  if (missingHeaders.length > 0) {
    return `Missing required column(s): ${missingHeaders.join(', ')}`;
  }
  return 'Invalid file headers.';
};

const parseNumeric = (value) => {
  const normalized = trimString(value).replace(/,/g, '');
  if (normalized === '') return { ok: false };
  if (/[^0-9.+-]/.test(normalized)) return { ok: false };
  const number = Number(normalized);
  if (Number.isNaN(number)) return { ok: false };
  return { ok: true, value: number };
};

const parseMonthEnd = (value) => {
  const date = dayjs(value, SUPPORTED_DATE_FORMATS, true);
  if (!date.isValid()) return null;
  return date;
};

const parseDate = (value) => {
  const date = dayjs(value, SUPPORTED_DATE_FORMATS, true);
  return date.isValid() ? date : null;
};

const csvToRows = async (file, expectedHeaders) => {
  const text = await file.text();
  const results = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  const headers = results.meta.fields || [];
  if (getMissingHeaders(headers, expectedHeaders).length > 0) {
    throw new Error(buildHeaderError(headers, expectedHeaders));
  }

  if (results.errors?.length) {
    throw new Error(results.errors[0].message || 'Invalid CSV file.');
  }

  return results.data;
};

const readTabularFile = async (file, expectedHeaders) => {
  const extension = file.name.split('.').pop().toLowerCase();
  if (extension === 'csv') {
    return csvToRows(file, expectedHeaders);
  }
  throw new Error('invalid File formate');
};

const validateRequired = (value, fieldName, rowNumber, errors) => {
  if (trimString(value) === '') {
    errors.push(`${fieldName} is missing at row ${rowNumber}`);
    return false;
  }
  return true;
};

const validateMonthEnd = (value, fieldName, rowNumber, errors) => {
  const parsedDate = parseMonthEnd(value);
  if (!parsedDate) {
    errors.push(`${fieldName} format is invalid at row ${rowNumber}. Use DD-MMM-YYYY.`);
    return null;
  }
  if (parsedDate.date() !== parsedDate.daysInMonth()) {
    errors.push(`${fieldName} must be the last day of month at row ${rowNumber}`);
    return null;
  }
  return parsedDate.format('YYYY-MM-DD');
};

const validateDateValue = (value, fieldName, rowNumber, errors) => {
  const date = parseDate(value);
  if (!date) {
    errors.push(`${fieldName} is missing at row ${rowNumber}`);
    return null;
  }
  return date.format('YYYY-MM-DD');
};

const extractDateFromText = (value) => {
  const text = trimString(value);
  if (!text) return null;

  const match = text.match(/\b\d{1,2}-[A-Za-z]{3}-\d{4}\b/);
  return match ? parseDate(match[0]) : parseDate(text);
};

export const normalizeRedmineRows = async (file) => {
  const headers = [
    'project_id',
    'employee_id',
    'activity',
    'efforts',
    'efforts_date',
    'month_end_date',
    'source',
    'comments',
    'darpan_effort_id',
    'jira_status',
    'jira_issue_id',
  ];

  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const projectId = validateRequired(row.project_id, 'Project Id', rowNumber, rowErrors) ? Number(row.project_id) : null;
    const employeeId = validateRequired(row.employee_id, 'Employee Id', rowNumber, rowErrors) ? trimString(row.employee_id) : null;
    const efforts = parseNumeric(row.efforts);
    if (!efforts.ok) rowErrors.push(`Log time is missing at row ${rowNumber}`);
    const effortsDate = validateDateValue(row.efforts_date, 'Efforts date', rowNumber, rowErrors);
    const monthEndDate = validateMonthEnd(row.month_end_date, 'Month end date', rowNumber, rowErrors);
    const source = validateRequired(row.source, 'Source', rowNumber, rowErrors) ? Number(row.source) : null;

    if (row.darpan_effort_id === undefined || row.darpan_effort_id === null || trimString(row.darpan_effort_id) === '') {
      row.darpan_effort_id = 0;
    }

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return null;
    }

    return {
      projectId,
      employeeId,
      activity: trimString(row.activity),
      efforts: efforts.value,
      effortsDate,
      monthEndDate,
      source,
      comments: trimString(row.comments),
      darpanEffortId: Number(row.darpan_effort_id || 0),
      jiraStatus: trimString(row.jira_status),
      jiraIssueId: trimString(row.jira_issue_id),
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

export const normalizeLeaveRows = async (file) => {
  const headers = ['leave_date', 'employee_id', 'total_leave', 'month_end_date'];
  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const leaveDate = validateDateValue(row.leave_date, 'Leave date', rowNumber, rowErrors);
    const employeeId = validateRequired(row.employee_id, 'Employee Id', rowNumber, rowErrors) ? trimString(row.employee_id) : null;
    const totalLeave = parseNumeric(row.total_leave);
    if (!totalLeave.ok) rowErrors.push(`No. of leave is missing at row ${rowNumber}`);
    const monthEndDate = validateMonthEnd(row.month_end_date, 'Month end date', rowNumber, rowErrors);

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return null;
    }

    return {
      leaveDate,
      employeeId,
      totalLeave: totalLeave.value,
      monthEndDate,
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

export const normalizeHolidayRows = async (file) => {
  const headers = ['holiday_date'];
  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const holidayDate = validateDateValue(row.holiday_date, 'Holiday date', rowNumber, rowErrors);
    const rawFinancialYear = row.financial_year ?? row.finacial_year;
    const financialYear = validateRequired(rawFinancialYear, 'Financial year', rowNumber, rowErrors)
      ? trimString(rawFinancialYear)
      : null;

    if (!holidayDate || !financialYear) {
      errors.push(...rowErrors);
      return null;
    }

    const holidayYear = dayjs(holidayDate).year();
    const month = dayjs(holidayDate).month() + 1;
    const expectedFinancialYear = month >= 4
      ? `${holidayYear}-${holidayYear + 1}`
      : `${holidayYear - 1}-${holidayYear}`;

    if (financialYear !== expectedFinancialYear) {
      errors.push(`Please check the financial year at row ${rowNumber}`);
      return null;
    }

    return {
      holidayDate,
      financialYear,
      holidayYear,
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

export const normalizeSalaryRows = async (file) => {
  const headers = ['employee_darpan_id', 'employee_id', 'employee_name', 'salary', 'month_end_date'];
  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const employeeDarpanId = parseNumeric(row.employee_darpan_id);
    if (!employeeDarpanId.ok) rowErrors.push(`Employee Darpan Id is missing at row ${rowNumber}`);
    const employeeId = validateRequired(row.employee_id, 'Employee Id', rowNumber, rowErrors) ? trimString(row.employee_id) : null;
    const employeeName = validateRequired(row.employee_name, 'Employee Name', rowNumber, rowErrors) ? trimString(row.employee_name) : null;
    const salary = parseNumeric(row.salary);
    if (!salary.ok) rowErrors.push(`Employee Salary is missing at row ${rowNumber}`);
    const monthEndDate = validateMonthEnd(row.month_end_date, 'Month end date', rowNumber, rowErrors);

    if (monthEndDate) {
      const fileMonth = dayjs(monthEndDate).month();
      const currentMonth = dayjs().month();
      if (fileMonth >= currentMonth) {
        rowErrors.push(`You can not upload salary for current and future months at row ${rowNumber}`);
      }
    }

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return null;
    }

    return {
      employeeDarpanId: employeeDarpanId.value,
      employeeId,
      employeeName,
      salary: salary.value,
      monthEndDate,
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

export const normalizeProjectExpenseRows = async (file) => {
  const headers = [
    'project_id',
    'project_name',
    'travel_and_accomodation',
    'other_cost',
    'consultant_cost',
    'software_hardware_cost',
    'month_end_date',
  ];
  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const projectId = parseNumeric(row.project_id);
    if (!projectId.ok) rowErrors.push(`Project Id is missing at row ${rowNumber}`);
    const projectName = validateRequired(row.project_name, 'Project Name', rowNumber, rowErrors) ? trimString(row.project_name) : null;
    const travel = parseNumeric(row.travel_and_accomodation);
    if (!travel.ok) rowErrors.push(`Travel and Accommodation Expense is missing at row ${rowNumber}`);
    const otherCost = parseNumeric(row.other_cost);
    if (!otherCost.ok) rowErrors.push(`Other Expense is missing at row ${rowNumber}`);
    const consultantCost = parseNumeric(row.consultant_cost);
    if (!consultantCost.ok) rowErrors.push(`Consultant Expense is missing at row ${rowNumber}`);
    const softwareCost = parseNumeric(row.software_hardware_cost);
    if (!softwareCost.ok) rowErrors.push(`Software/Hardware Expense is missing at row ${rowNumber}`);
    const monthEndDate = validateMonthEnd(row.month_end_date, 'Month end date', rowNumber, rowErrors);

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return null;
    }

    const totalExpense = travel.value + otherCost.value + consultantCost.value + softwareCost.value;

    return {
      projectId: projectId.value,
      projectName,
      travelAndAccomodation: travel.value,
      otherCost: otherCost.value,
      consultantCost: consultantCost.value,
      softwareHardwareCost: softwareCost.value,
      totalExpense,
      monthEndDate,
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

const resolveActivityValue = (activity, activityLookup) => {
  const normalizedActivity = trimString(activity);
  if (!normalizedActivity) return null;

  if (/^\d+$/.test(normalizedActivity)) {
    return Number(normalizedActivity);
  }

  const lookupValue = activityLookup instanceof Map
    ? activityLookup.get(normalizedActivity.toLowerCase())
      || activityLookup.get(normalizedActivity)
    : null;

  if (lookupValue === undefined || lookupValue === null || lookupValue === '') {
    return normalizedActivity;
  }

  const numericValue = Number(lookupValue);
  return Number.isNaN(numericValue) ? lookupValue : numericValue;
};

export const normalizeJiraRows = async (file, activityLookup = null) => {
  const headers = [
    'project_id',
    'employee_id',
    'efforts_date',
    'activity',
    'efforts',
    'comments',
    'source',
    'month_end_date',
  ];
  const rows = await readTabularFile(file, headers);
  const errors = [];
  const normalizedRows = rows.map((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const projectId = parseNumeric(row.project_id);
    if (!projectId.ok) rowErrors.push(`Project Id is missing at row ${rowNumber}`);
    const employeeId = validateRequired(row.employee_id, 'Employee Id', rowNumber, rowErrors) ? trimString(row.employee_id) : null;
    const effortsDate = validateDateValue(row.efforts_date, 'Effort date', rowNumber, rowErrors);
    const activity = validateRequired(row.activity, 'Activity', rowNumber, rowErrors)
      ? resolveActivityValue(row.activity, activityLookup)
      : null;
    const efforts = parseNumeric(row.efforts);
    if (!efforts.ok) rowErrors.push(`Effort is missing at row ${rowNumber}`);
    const source = parseNumeric(row.source);
    if (!source.ok) rowErrors.push(`Source is missing at row ${rowNumber}`);
    const monthEndDate = validateMonthEnd(row.month_end_date, 'Month end date', rowNumber, rowErrors);

    if (rowErrors.length) {
      errors.push(...rowErrors);
      return null;
    }

    return {
      projectId: projectId.value,
      employeeId,
      effortsDate,
      activity,
      efforts: efforts.value,
      comments: trimString(row.comments),
      source: source.value,
      monthEndDate,
    };
  }).filter(Boolean);

  return { rows: normalizedRows, errors };
};

export const normalizeLeaveTestRows = async (file) => {
  const text = await file.text();
  const results = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  const headers = results.meta.fields || [];
  const legacyHeaders = ['Leave Date', 'Employee', 'Status', 'Leaves'];
  const sampleHeaders = [
    'Month-end Date',
    'Employee ID',
    'Leave type',
    'From',
    'To',
    'Days/Hours Taken',
    'Approval Status',
    'Date - Duration and Session',
  ];

  const hasLegacyHeaders = getMissingHeaders(headers, legacyHeaders).length === 0;
  const hasSampleHeaders = getMissingHeaders(headers, sampleHeaders).length === 0;

  if (!hasLegacyHeaders && !hasSampleHeaders) {
    throw new Error(buildHeaderError(headers, legacyHeaders));
  }

  const rows = [];
  const errors = [];
  const holidayLookup = new Map();

  results.data.forEach((row, index) => {
    const rowNumber = index + 2;
    const parsedLeaveDate = hasSampleHeaders
      ? extractDateFromText(row['From']) || extractDateFromText(row['Date - Duration and Session']) || extractDateFromText(row['Month-end Date'])
      : parseDate(row['Leave Date']);
    const employeeId = hasSampleHeaders
      ? trimString(row['Employee ID'])
      : trimString(row.Employee).split('-')[0].trim();
    const leaves = hasSampleHeaders ? trimString(row['Days/Hours Taken']) : trimString(row.Leaves);
    const approvalStatus = hasSampleHeaders ? trimString(row['Approval Status']) : trimString(row.Status);

    if (approvalStatus === 'Cancelled' || approvalStatus === 'Rejected') {
      return;
    }

    if (!parsedLeaveDate) {
      errors.push(`Leave date is missing at row ${rowNumber}`);
      return;
    }

    if (!employeeId) {
      errors.push(`Employee Id is missing at row ${rowNumber}`);
      return;
    }

    if (hasSampleHeaders) {
      const totalLeave = parseNumeric(leaves);
      if (!totalLeave.ok) {
        errors.push(`No. of leave is missing at row ${rowNumber}`);
        return;
      }

      const monthEndDate = validateMonthEnd(row['Month-end Date'], 'Month-end Date', rowNumber, errors);
      if (!monthEndDate) return;

      rows.push({
        leaveDate: parsedLeaveDate.format('YYYY-MM-DD'),
        employeeId,
        totalLeave: totalLeave.value,
        monthEndDate,
      });
      return;
    }

    const leaveRows = leaves.split('},').map((entry) => entry.replace(/[{}]/g, '').trim()).filter(Boolean);
    leaveRows.forEach((entry) => {
      const [dateToken, durationToken] = entry.split(',');
      const leaveDate = trimString(dateToken).replace(/^Date:/, '');
      const duration = trimString(durationToken).replace(/^Duration:/, '');
      const leaveDay = parseDate(leaveDate);
      if (!leaveDay) return;

      const monthEndDate = leaveDay.endOf('month').format('YYYY-MM-DD');
      if (monthEndDate !== parsedLeaveDate.endOf('month').format('YYYY-MM-DD')) return;

      const normalizedLeave = duration === 'Half Day' ? 0.5 : 1;
      const hashKey = `${leaveDay.format('YYYY-MM-DD')}::${employeeId}`;
      if (!holidayLookup.has(hashKey)) {
        holidayLookup.set(hashKey, true);
        rows.push({
          leaveDate: leaveDay.format('YYYY-MM-DD'),
          employeeId,
          totalLeave: normalizedLeave,
          monthEndDate,
        });
      }
    });
  });

  return { rows, errors };
};
